import React, { useCallback, useContext, useMemo, useState } from "react";
import { Box, Button, Dialog, DialogContent, TextField } from '@mui/material'
import { DialogFooter, DialogHeader } from 'components/dialog/DialogHeader';
import { initDialogData } from "./initData";
import { generateMenuComboItem, loadMSData, validate } from "utils";
import PaperComponent from "components/PaperComponent";
import { useEffect } from "react";
import { BoxFC, BoxFR } from "components/BoxCustom";
import { GlobalStateContext } from "contexts/GlobalStateContext";
import { alertConfirmDelete, alertError, alertWarning } from "components/Alert";
import { poiApiNm, poiColNm } from "constants/POIConstant";
import GoogleMapWrapper from "components/GoogleMapWrapper/GoogleMapWrapper";
import TagBox from "components/TagBox";
import { endAdornmentProps } from "utils/bahtProps";
import { HistoryRounded, RouteRounded } from "resource/muiImport";
import LogTableDialog from "components/LogTableDialog/LogTableDialog";


const excludeKeys = ["POILogId", "POIId", "ModDte", "ModAcc"]
let initMapCenter = null
let oData = null
const POIDialog = ({ selectedId, dialogOpen, setDialogOpen, onFinish, lastFilter }) => {

  const { ax, msData, setMSData } = useContext(GlobalStateContext)
  const [dialogData, setDialogData] = useState({ ...initDialogData })
  const [tagData, setTagData] = useState([])
  const [dialogHistoryOpen, setDialogHistoryOpen] = useState(false)

  const latLngData = useMemo(() => {
    return {
      lat: dialogData.Lat,
      lng: dialogData.Lng,
      rad: dialogData.Rad,
      points: dialogData.points
    }
  }, [dialogData.Lat, dialogData.Lng, dialogData.Rad, dialogData.points])

  const setLatLngData = useCallback((latLngData) => {
   
    const { lat, lng, rad, points } = latLngData
    setDialogData((o) => ({ ...o, Lat: lat, Lng: lng, Rad: rad, points: points }))
  }, [])

  const validation = useMemo(() => ({
    POITypId: dialogData.POITypId === null,
    Name: dialogData.Name === "",
    SName: dialogData.SName === "",
  }), [dialogData])

  const textFieldProp = useCallback((name, isRequired) => ({
    variant: "outlined",
    size: "small",
    name: name,
    label: poiColNm[name],
    error: validation[name],
    value: dialogData[name] || "",
    required: isRequired,
    fullWidth: true,
    onChange: (e) => { setDialogData((o) => ({ ...o, [name]: e.target.value })) }
  }), [dialogData, setDialogData, validation])

  const insertUpdatePOI = useCallback((data) => {
    const apiName = selectedId === 0 ? poiApiNm.insertPOI : poiApiNm.updatePOI
    let polyStr = null
    if (dialogData.points && dialogData.points.length > 0) {
      const points = dialogData.points
      points.push(points[0])
      polyStr = `POLYGON((${dialogData.points.map(point => `${point.lat} ${point.lng}`).join(",")}))`
    }

    let altNms = null
   
    if (dialogData.AltNms !== oData.AltNms) {
      if (dialogData.AltNms.trim() !== "") {
        altNms = dialogData.AltNms.split(",").map(altNm => altNm.trim())
      } else {
        altNms = []
      }
    }
    const postData = {
      ...dialogData,
      TagIds: tagData.map(tag => tag.id),
      PolyStr: polyStr,
      getArgs: lastFilter,
      AltNms: altNms
    }
    ax.post(apiName, postData).then(value => {
      if (value.data) {
        setDialogOpen(false)
        if (onFinish) {
          onFinish(value.data)
        }
      }
    })
  }, [ax, lastFilter, dialogData, onFinish, selectedId, setDialogOpen, tagData])

  const deletePOI = useCallback((id) => {
    alertConfirmDelete(() => {
      ax.post(poiApiNm.deletepoi, { POIId: id, getArgs: lastFilter }).then(value => {
        if (value.data) {
          onFinish(value.data)
          setDialogOpen(false)
          loadMSData(ax, setMSData)
        }
      })
    })
  }, [ax, lastFilter, onFinish, setDialogOpen, setMSData])

  const handleDialogOk = useCallback(() => {

    if (validate(validation)) {
      insertUpdatePOI(dialogData)
    }
  }, [dialogData, insertUpdatePOI, validation])

  const handleGetDistance = useCallback(() => {
    const companyPOIId = msData.serverData.CompanyData.POIId
    if (!companyPOIId) {
      alertError("กรุณาบันทึกสถานที่บริษัทในหน้า 6.3 การจัดการข้อมูลระบบ > ข้อมูลบริษัท ก่อน")
      return
    }
   
    const postData = {
      Destination: { latitude: dialogData.Lat, longitude: dialogData.Lng },
    }
    ax.post(poiApiNm.getDistanceFromCompany, postData).then(value=>{
      if (value.data) {
        setDialogData((o) => ({ ...o, Dist: value.data }))
      } 
    })  
  }, [dialogData.Lat, dialogData.Lng, ax, msData.serverData.CompanyData.POIId])

  useEffect(() => {
    if (dialogOpen && selectedId > 0) {

      ax.post(poiApiNm.getPOI, { POIId: selectedId, withDetail: 1 }).then(value => {
        if (value.data && value.data.length > 0) {
          const data = value.data[0]
          if (data.PolyStr) {
            const points = data.PolyStr.replace("POLYGON((", "").replace("))", "").split(",").map(point => {
              const latLng = point.trim().split(" ")
              return { lat: +latLng[0], lng: +latLng[1] }
            })
            data.points = points
          }

          if (data.lat && data.lng) {
            initMapCenter = { lat: data.lat, lng: data.lng }
          } else if (data.points && data.points.length > 0) {
            const points = data.points
            // this code is generate from 
            const lat = points.reduce((acc, point) => acc + point.lat, 0) / points.length
            const lng = points.reduce((acc, point) => acc + point.lng, 0) / points.length
            initMapCenter = { lat, lng }
          }
         

          oData = { ...value.data[0] }
          setDialogData({ ...value.data[0] })
          if (value.data[0].TagIds) {
            setTagData(value.data[0].TagIds.map(tagId => ({ id: tagId, label: msData.tagObj[tagId] })))
          }
        } else {
          oData = { ...initDialogData }
          setDialogData({ ...initDialogData })
          alertWarning("ไม่พบข้อมูล")
        }
      })
    } else {
      initMapCenter = null
      oData = { ...initDialogData }
      setDialogData({ ...initDialogData })
      setTagData([])
    }
  }, [ax, dialogOpen, selectedId, msData.tagObj])
  // useEffect(() => {
  //   if (dialogOpen === false || !selectedId) {
  //     setIsEditReject(false)
  //     setDialogData({ ...initDialogData, Items: [{ ...initBookingItems }] })
  //   }
  // }, [dialogOpen, selectedId])

  return (
    <Dialog
      open={dialogOpen}
      PaperComponent={PaperComponent}
      aria-labelledby="draggable-dialog-title"
      fullWidth maxWidth='lg' >
      <DialogHeader handleDialogClose={() => setDialogOpen(false)}
        title="สถานที่"
        onDeleteClick={selectedId > 0 ? () => deletePOI(selectedId) : null}
      >
        {selectedId > 0 &&
          <Button sx={{ ml: 1 }} variant="contained" color="success" onClick={() => setDialogHistoryOpen(true)}>
            <HistoryRounded sx={{ mr: 1 }} />ประวัติ
          </Button>}
      </DialogHeader>
      <DialogContent>
        <BoxFR mt={2}>
          <BoxFC width={250} sx={{ gap: 1 }}>
            <TextField {...textFieldProp("POITypId", true)} select >
              {generateMenuComboItem(msData.poiTypeCombo)}
            </TextField>
            <TextField {...textFieldProp("Name", true)} />
            <TextField {...textFieldProp("SName", true)} />
            <TextField {...textFieldProp("AltNms", false)} multiline row={2} helperText="คั่นชื่อด้วยเครื่องหมายจุลภาค (,)" />
            <TextField {...textFieldProp("InvNm", false)} helperText="(ไม่ระบุจะใช้ชื่อย่อในการพิมพ์ใบแจ้งหนี้)" />
            <TextField {...textFieldProp("Tel")} />
            <TextField {...textFieldProp("Prty")} type="number" />
            <TextField {...textFieldProp("Addr")} multiline rows={4} />
            <BoxFR>
              <TextField {...textFieldProp("Dist")} {...endAdornmentProps("กม.")} fullWidth/>
              <Button variant="contained" fullWidth sx={{px: 1}} onClick={handleGetDistance}><RouteRounded sx={{mr:1}}/>คำนวณ</Button>
            </BoxFR>
            <TextField {...textFieldProp("Rmk")} multiline rows={3} />

            <TagBox value={tagData} setValue={setTagData} tblAbbr="LO" />
          </BoxFC>
          <Box alignSelf="stretch" flex={1}>
            <GoogleMapWrapper latLngData={latLngData} setLatLngData={setLatLngData} initCenter={initMapCenter} />
          </Box>
        </BoxFR>
      </DialogContent>
      <LogTableDialog
        tableName="poi_log"
        idColNm="POIId"
        idValue={selectedId}
        excludeKeys={excludeKeys}
        colNm={poiColNm}
        title="ประวัติการแก้ไขข้อมูลสถานที่"
        dialogOpen={dialogHistoryOpen}
        setDialogOpen={setDialogHistoryOpen}
      />
      <DialogFooter okText={selectedId > 0 ? "แก้ไขข้อมูล" : "เพิ่มข้อมูล"}
        handleDialogClose={() => setDialogOpen(false)}
        handleDialogOk={handleDialogOk} />
    </Dialog>
  )
}

export default React.memo(POIDialog)